/**
 * LoveAll Scoring Engine — runs entirely in the browser.
 *
 * Supports two presets (both use the Standard Golden Point rule — first to N
 * wins the game; at N-1 vs N-1 the next point decides, no win-by-2):
 *  - BWF_3x15 ("Sprint"): first to 15
 *  - CLASSIC_21        : first to 21 (20-20 → next point wins 21-20)
 *
 * Best-of: 1 or 3.
 */

export const PRESETS = {
  BWF_3x15: {
    id: 'BWF_3x15',
    name: 'BWF 3x15 Sprint',
    pointsToWin: 15,
    deuceStart: 14,
    hardCap: 21,
    intervalAt: 8,
    bestOf: 3,
    decidingSwapAt: 8,
  },
  CLASSIC_21: {
    id: 'CLASSIC_21',
    name: 'Classic 21',
    pointsToWin: 21,
    deuceStart: 20,
    hardCap: 30,
    intervalAt: 11,
    bestOf: 1,
    decidingSwapAt: 11,
  },
};

/**
 * Build a custom Classic-style preset with the chosen points-per-game and best-of.
 * Used when the user picks "Classic" mode and selects 11/15/21 + 1/3/5.
 */
export function buildClassicPreset({ pointsPerGame = 21, bestOf = 1 } = {}) {
  const pts = Number(pointsPerGame) || 21;
  return {
    id: 'CLASSIC_CUSTOM',
    name: `Classic ${pts}`,
    pointsToWin: pts,
    deuceStart: pts - 1,
    hardCap: pts + Math.max(8, Math.floor(pts * 0.4)),  // cap headroom
    // BWF-aligned drinks break: 11 in 21-pt, 8 in 15-pt, 6 in 11-pt
    intervalAt: Math.floor(pts / 2) + 1,
    bestOf: Number(bestOf) || 1,
    decidingSwapAt: Math.floor(pts / 2) + 1,
  };
}

/**
 * Evaluate game state after a score change.
 * Returns:
 *  {
 *    gameWinner: 'A' | 'B' | null,
 *    matchWinner: 'A' | 'B' | null,
 *    intervalTriggered: boolean,  // true the moment leading score first hits intervalAt
 *    sideSwapTriggered: boolean,  // 3rd-set side swap at decidingSwapAt
 *  }
 */
export function evaluateState({ preset, games, currentGameIdx, intervalServedFor, swapServedFor }) {
  const cfg = preset;
  const g = games[currentGameIdx];
  const { scoreA, scoreB } = g;
  const leading = Math.max(scoreA, scoreB);

  // Game winner — Standard Golden Point rule: first to pointsToWin wins.
  // At 20-20 (deuce score) the very next point wins the game: 21-20 ends it.
  let gameWinner = null;
  if (scoreA >= cfg.pointsToWin && scoreA > scoreB) gameWinner = 'A';
  else if (scoreB >= cfg.pointsToWin && scoreB > scoreA) gameWinner = 'B';

  // Match winner from games tally
  let matchWinner = null;
  if (gameWinner) {
    const gamesWonA = games.filter(x => x.winner === 'A').length + (gameWinner === 'A' ? 1 : 0);
    const gamesWonB = games.filter(x => x.winner === 'B').length + (gameWinner === 'B' ? 1 : 0);
    const gamesToWin = Math.floor(cfg.bestOf / 2) + 1;
    if (gamesWonA >= gamesToWin) matchWinner = 'A';
    else if (gamesWonB >= gamesToWin) matchWinner = 'B';
  }

  // Interval trigger (once per game when leading score first hits intervalAt)
  const intervalTriggered =
    !gameWinner &&
    leading === cfg.intervalAt &&
    intervalServedFor !== currentGameIdx;

  // 3rd set deciding side swap
  const isDeciderSet = currentGameIdx === cfg.bestOf - 1 && cfg.bestOf > 1;
  const sideSwapTriggered =
    !gameWinner &&
    isDeciderSet &&
    leading === cfg.decidingSwapAt &&
    swapServedFor !== currentGameIdx;

  return { gameWinner, matchWinner, intervalTriggered, sideSwapTriggered };
}

/** Build initial match state */
export function newMatchState({ presetId, preset, matchType, sideA, sideB, partnerA, partnerB }) {
  const cfg = preset || PRESETS[presetId] || PRESETS.BWF_3x15;
  return {
    id: `m_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
    presetId: cfg.id,
    preset: cfg,            // embed full config for forward compat
    matchType: matchType || 'singles',
    sideA: sideA?.trim() || 'Side A',
    sideB: sideB?.trim() || 'Side B',
    partnerA: partnerA?.trim() || '',
    partnerB: partnerB?.trim() || '',
    bestOf: cfg.bestOf,
    pointsToWin: cfg.pointsToWin,
    games: [{ scoreA: 0, scoreB: 0, winner: null }],
    currentGameIdx: 0,
    intervalServedFor: -1,
    swapServedFor: -1,
    serverSide: 'A',
    sideSwapped: false,
    startedAt: new Date().toISOString(),
    completedAt: null,
    durationSec: 0,
    winner: null,
  };
}

/** Apply a +1 point; returns new state and flags. */
export function applyPoint(state, side /* 'A' | 'B' */) {
  const preset = state.preset || PRESETS[state.presetId] || PRESETS.BWF_3x15;
  if (state.winner) return { state, flags: { matchWinner: state.winner } };
  const games = state.games.map((g, i) =>
    i === state.currentGameIdx
      ? { ...g, scoreA: side === 'A' ? g.scoreA + 1 : g.scoreA, scoreB: side === 'B' ? g.scoreB + 1 : g.scoreB }
      : g
  );
  const flags = evaluateState({
    preset,
    games,
    currentGameIdx: state.currentGameIdx,
    intervalServedFor: state.intervalServedFor,
    swapServedFor: state.swapServedFor,
  });

  let newState = { ...state, games, serverSide: side };

  if (flags.intervalTriggered) {
    newState.intervalServedFor = state.currentGameIdx;
  }
  if (flags.sideSwapTriggered) {
    newState.swapServedFor = state.currentGameIdx;
    newState.sideSwapped = !state.sideSwapped;
  }
  if (flags.gameWinner) {
    games[state.currentGameIdx].winner = flags.gameWinner;
    if (flags.matchWinner) {
      newState.winner = flags.matchWinner;
      newState.completedAt = new Date().toISOString();
      newState.durationSec = Math.round((Date.now() - new Date(state.startedAt).getTime()) / 1000);
    } else {
      newState.games = [...games, { scoreA: 0, scoreB: 0, winner: null }];
      newState.currentGameIdx = state.currentGameIdx + 1;
    }
  }
  return { state: newState, flags };
}

/** Undo a point. We rebuild from history isn't tracked here; treat as decrement. */
export function undoPoint(state, side) {
  if (state.winner) return state;
  const games = state.games.map((g, i) =>
    i === state.currentGameIdx
      ? {
          ...g,
          scoreA: side === 'A' ? Math.max(0, g.scoreA - 1) : g.scoreA,
          scoreB: side === 'B' ? Math.max(0, g.scoreB - 1) : g.scoreB,
        }
      : g
  );
  return { ...state, games };
}

/** Final brag card payload */
export function buildBragCard(state) {
  const lines = state.games.map((g) => `${g.scoreA}-${g.scoreB}`).join(', ');
  // Build display names that include doubles partners when present.
  const sideALabel = state.partnerA
    ? `${state.sideA} & ${state.partnerA}`
    : state.sideA;
  const sideBLabel = state.partnerB
    ? `${state.sideB} & ${state.partnerB}`
    : state.sideB;
  const winnerName = state.winner === 'A' ? sideALabel : sideBLabel;
  const loserName  = state.winner === 'A' ? sideBLabel : sideALabel;
  const presetName = state.preset?.name || PRESETS[state.presetId]?.name || 'Match';
  return {
    winnerName,
    loserName,
    scoreLine: lines,
    title: `${sideALabel} vs ${sideBLabel}`,
    presetName,
    durationSec: state.durationSec,
  };
}
